import { Navigate } from 'react-router-dom';

import { call } from '@spacefill/shared/src/utils/Backend';
import { NavigateWithParams } from '@spacefill/shared/src/utils/RouterUtils';

const OrdersList = async () =>
    import('../../pages/orders/list/List').then((module) => ({
        Component: module.OrdersList,
    }));
const OrdersImport = async () =>
    import('../../pages/orders/new/ImportOrders').then((module) => ({
        Component: module.LogisticManagementOrdersImport,
    }));

const OrdersRedirectView = async () =>
    import('../../pages/orders/RedirectView').then((module) => ({
        Component: module.LogisticManagementOrdersRedirectView,
    }));
const WarehousesOrdersView = async () =>
    import('../../pages/orders/view/View').then((module) => ({
        Component: module.LogisticManagementWarehousesOrdersView,
    }));

const NewOrderInit = async () =>
    import('../../pages/orders/new/Init').then((module) => ({
        Component: module.NewOrderInit,
    }));
const WarehousesNewOrdersEntry = async () =>
    import('../../pages/orders/new/Entry').then((module) => ({
        Component: module.LogisticManagementWarehousesNewOrdersEntry,
    }));
const WarehousesNewOrdersExit = async () =>
    import('../../pages/orders/new/Exit').then((module) => ({
        Component: module.LogisticManagementWarehousesNewOrdersExit,
    }));
const NewSale = async () =>
    import('../../pages/orders/new/NewSale').then((module) => ({
        Component: module.NewSale,
    }));
const SaleView = async () =>
    import('../../pages/orders/view/SaleView').then((module) => ({
        Component: module.SaleView,
    }));
const SalesList = async () =>
    import('../../pages/orders/list/SalesList').then((module) => ({
        Component: module.SalesList,
    }));

const OrdersRedirectEdit = async () =>
    import('../../pages/orders/RedirectEdit').then((module) => ({
        Component: module.LogisticManagementOrdersRedirectEdit,
    }));
const WarehousesEditOrders = async () =>
    import('../../pages/orders/edit/Edit').then((module) => ({
        Component: module.LogisticManagementWarehousesEditOrders,
    }));
const WarehousesEditOrdersRedirectEntryOrExit = async () =>
    import('../../pages/orders/edit/index').then((module) => ({
        Component: module.LogisticManagementWarehousesEditOrdersRedirectEntryOrExit,
    }));

const WarehousesOrdersAdjustments = async () =>
    import('../../pages/orders/Adjustments').then((module) => ({
        Component: module.LogisticManagementWarehousesOrdersAdjustments,
    }));
const UnconfirmedOrdersList = async () =>
    import('../../pages/unconfirmed-orders/List').then((module) => ({
        Component: module.LogisticManagementUnconfirmedOrdersList,
    }));
const UnconfirmedOrdersView = async () =>
    import('../../pages/unconfirmed-orders/View').then((module) => ({
        Component: module.LogisticManagementUnconfirmedOrdersView,
    }));

const AppointmentModalRoute = async () =>
    import('./AppointmentModalRoute').then((module) => ({
        Component: module.AppointmentModalRoute,
    }));

/*
 WARNING: if you modify this file, please check the regex on the context switch packages/external-console/src/utils/contextSwitch.js (const orderPagePatterns)
 */

const getOrderRoutes = (user) => {
    return [
        // LISTS
        {
            path: '/logistic-management/orders/',
            element: <Navigate to='/logistic-management/exit-orders/' />,
        },
        {
            path: '/logistic-management/warehouses/:warehouseId/orders/',
            element: (
                <NavigateWithParams
                    to={(params) => `/logistic-management/warehouses/${params.warehouseId}/exit-orders/`}
                />
            ),
        },
        {
            path: '/return-orders/',
            element: <Navigate to='/logistic-management/return-orders/' />,
        },
        {
            path: '/entry-orders/',
            element: <Navigate to='/logistic-management/entry-orders/' />,
        },
        {
            path: '/exit-orders/',
            element: <Navigate to='/logistic-management/exit-orders/' />,
        },
        {
            paths: [
                // Entries
                '/logistic-management/entry-orders/',
                '/logistic-management/entry-orders/view/:viewId',
                '/logistic-management/warehouses/:warehouseId/entry-orders/',
                '/logistic-management/warehouses/:warehouseId/entry-orders/view/:viewId',

                // Exits
                '/logistic-management/exit-orders/',
                '/logistic-management/exit-orders/view/:viewId',
                '/logistic-management/warehouses/:warehouseId/exit-orders/',
                '/logistic-management/warehouses/:warehouseId/exit-orders/view/:viewId',

                // Returns
                '/logistic-management/return-orders/',
                '/logistic-management/return-orders/view/:viewId',
                '/logistic-management/warehouses/:warehouseId/return-orders/',
                '/logistic-management/warehouses/:warehouseId/return-orders/view/:viewId',
            ],
            lazy: OrdersList,
            conditionFailedRedirect: '/',
            condition: !user?.carrierId,
        },

        // CREATION
        {
            paths: [
                '/logistic-management/entry-orders/init',
                '/logistic-management/warehouses/:warehouseId/entry-orders/init',
                '/logistic-management/exit-orders/init',
                '/logistic-management/warehouses/:warehouseId/exit-orders/init',
                '/logistic-management/return-orders/init',
                '/logistic-management/warehouses/:warehouseId/return-orders/init',
                '/sales/init',
            ],
            lazy: NewOrderInit,
            conditionFailedRedirect: '/',
            condition: !user?.carrierId,
        },
        {
            paths: [
                '/logistic-management/warehouses/:warehouseId/entry-orders/new',
                '/logistic-management/warehouses/:warehouseId/orders/new-entry',
                '/logistic-management/warehouses/:warehouseId/return-orders/new',
            ],
            lazy: WarehousesNewOrdersEntry,
            conditionFailedRedirect: '/',
            condition: !user?.carrierId,
        },
        {
            paths: [
                '/logistic-management/warehouses/:warehouseId/exit-orders/new',
                '/logistic-management/warehouses/:warehouseId/orders/new-exit',
            ],
            lazy: WarehousesNewOrdersExit,
            conditionFailedRedirect: '/',
            condition: !user?.carrierId,
        },

        // Shipper sales
        {
            paths: ['/sales/new'],
            lazy: NewSale,
            conditionFailedRedirect: '/',
            condition: !!user.customerId && user.features.automations.orderRoutingEnabled && !user?.carrierId,
        },
        {
            paths: ['/sales/edit/:saleId'],
            lazy: NewSale,
            conditionFailedRedirect: '/',
            condition: !!user.customerId && user.features.automations.orderRoutingEnabled && !user?.carrierId,
        },
        {
            paths: ['/sales/:saleId'],
            lazy: SaleView,
            conditionFailedRedirect: '/',
            condition: !!user.customerId && user.features.automations.orderRoutingEnabled && !user?.carrierId,
        },
        {
            paths: ['/sales/'],
            lazy: SalesList,
            conditionFailedRedirect: '/',
            condition: !!user.customerId && user.features.automations.orderRoutingEnabled && !user?.carrierId,
        },

        // VIEW
        {
            paths: [
                '/orders/:orderId/',
                '/logistic-management/orders/:orderId/',
                '/logistic-management/warehouses/:warehouseId/orders/:orderId/',
            ],
            lazy: OrdersRedirectView,
            condition: !user?.carrierId,
        },

        {
            path: '/entry-orders/:orderId/',
            lazy: WarehousesOrdersView,
            children: [
                {
                    path: 'appointment/:appointmentId',
                    lazy: AppointmentModalRoute,
                    loader: appointmentFromAppointmentIdLoader,
                },
            ],
            redirectFrom: ['/logistic-management/warehouses/:warehouseId/entry-orders/:orderId/'],
        },
        {
            path: '/exit-orders/:orderId/',
            lazy: WarehousesOrdersView,
            children: [
                {
                    path: 'appointment/:appointmentId',
                    lazy: AppointmentModalRoute,
                    loader: appointmentFromAppointmentIdLoader,
                },
            ],
            redirectFrom: ['/logistic-management/warehouses/:warehouseId/exit-orders/:orderId/'],
        },
        {
            path: '/return-orders/:orderId/',
            lazy: WarehousesOrdersView,
            children: [
                {
                    path: 'appointment/:appointmentId',
                    lazy: AppointmentModalRoute,
                    loader: appointmentFromAppointmentIdLoader,
                },
            ],
            redirectFrom: ['/logistic-management/warehouses/:warehouseId/return-orders/:orderId/'],
        },

        // EDIT
        {
            path: '/logistic-management/orders/:orderId/edit',
            lazy: OrdersRedirectEdit,
            condition: !user?.carrierId,
        },
        {
            path: '/logistic-management/warehouses/:warehouseId/orders/:orderId/edit',
            lazy: WarehousesEditOrdersRedirectEntryOrExit,
            condition: !user?.carrierId,
        },
        {
            paths: [
                '/logistic-management/warehouses/:warehouseId/exit-orders/:orderId/edit',
                '/logistic-management/warehouses/:warehouseId/entry-orders/:orderId/edit',
            ],
            lazy: WarehousesEditOrders,
            condition: !user?.carrierId,
        },

        // Misc
        {
            path: '/logistic-management/orders/import/',
            lazy: OrdersImport,
            condition: !user?.carrierId,
        },

        // LSP ponly
        {
            path: '/logistic-management/unconfirmed-orders/',
            lazy: UnconfirmedOrdersList,
            condition: !!user.logisticProviderId && !user?.carrierId,
        },
        {
            path: '/logistic-management/unconfirmed-exits/',
            lazy: UnconfirmedOrdersList,
            condition: !!user.logisticProviderId && !user?.carrierId,
        },
        {
            path: '/logistic-management/unconfirmed-entries/',
            lazy: UnconfirmedOrdersList,
            condition: !!user.logisticProviderId && !user?.carrierId,
        },
        {
            paths: [
                '/logistic-management/unconfirmed-orders/:unconfirmedOrderId/',
                '/logistic-management/unconfirmed-entries/:unconfirmedOrderId/',
                '/logistic-management/unconfirmed-exits/:unconfirmedOrderId/',
            ],
            lazy: UnconfirmedOrdersView,
            condition: !!user.logisticProviderId && !user?.carrierId,
        },
        {
            paths: [
                '/logistic-management/warehouses/:warehouseId/entry-orders/:orderId/order-adjustment',
                '/logistic-management/warehouses/:warehouseId/exit-orders/:orderId/order-adjustment',
            ],
            lazy: WarehousesOrdersAdjustments,
            condition: !!user.logisticProviderId && !user?.carrierId,
        },
    ];
};

const appointmentFromAppointmentIdLoader = async ({ params: { appointmentId } }) => {
    const appointment = await call(`appointments/${appointmentId}`);
    return { appointment };
};

export { getOrderRoutes };
